// /src/pages/Consents/ListConsentedContacts.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import DataTable from "react-data-table-component";
import {
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  Modal,
  useTheme,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";

// Import Sub-components
import ConsentedContactForm from "./ConsentedContactForm";
import ImportConsentModal from "./ImportConsentModal";

export default function ListConsentedContacts() {
  const theme = useTheme();

  const [contactsData, setContactsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [triggerFetch, setTriggerFetch] = useState(false);

  // Modal States
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Permission states
  const [canView, setCanView] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // 1. Check Permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        // Adjust permission keys based on your system
        const viewPerm = await hasPermission("consents", "view");
        const addPerm = await hasPermission("consents", "add");
        const deletePerm = await hasPermission("consents", "delete");

        setCanView(viewPerm);
        setCanAdd(addPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  // 2. Fetch Data
  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      // Calls the endpoint defined in your python: get_all_subscriptions_controller
      const response = await axiosInstance.get(`/api/user/subscriptions`);
      const responseData = response.data.data || [];
      setContactsData(responseData);
      setFilteredData(responseData);
      setError(null);
    } catch (err) {
      console.error("Error fetching consents:", err);
      setError("Failed to fetch consented contacts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Consented Contacts | KlonIT";
    if (canView && !checkingPermissions) {
      fetchContacts();
    }
  }, [triggerFetch, canView, checkingPermissions, fetchContacts]);

  // 3. Search Filter
  useEffect(() => {
    const lower = searchText.toLowerCase();
    const filtered = contactsData.filter(
      (item) =>
        (item.email && item.email.toLowerCase().includes(lower)) ||
        (item.phone && item.phone.toLowerCase().includes(lower)) ||
        item.source?.toLowerCase().includes(lower)
    );
    setFilteredData(filtered);
  }, [searchText, contactsData]);

  // 4. Handle Delete (Unsubscribe)
  const handleDelete = (id, identifier) => {
    Swal.fire({
      title: "Revoke Consent?",
      html: `Are you sure you want to unsubscribe <b>${identifier}</b>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, Unsubscribe",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await axiosInstance.post(`/api/user/unsubscribe`, { subscription_id: id });
          Swal.fire("Unsubscribed!", "Contact has been removed.", "success");
          setTriggerFetch((prev) => !prev);
        } catch (err) {
          Swal.fire("Error", "Failed to unsubscribe contact.", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleSaveSuccess = (msg) => {
    setTriggerFetch((prev) => !prev);
    setError({ message: msg, type: "success" });
    setIsFormOpen(false);
    setIsImportOpen(false);
  };

  // 5. DataTable Columns
  const columns = [
    {
      name: "Date",
      selector: (row) => row.created_at,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <Typography variant="caption">
          {row.created_at ? new Date(row.created_at).toLocaleDateString() : "-"}
        </Typography>
      ),
    },
    {
      name: "Contact Info",
      grow: 2,
      cell: (row) => (
        <Box display="flex" flexDirection="column" py={1}>
          {row.email && (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               📧 {row.email}
            </Typography>
          )}
          {row.phone && (
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
               📱 {row.phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      name: "Consents",
      grow: 1,
      cell: (row) => (
        <Box display="flex" gap={1}>
          {row.promotional_consent && (
            <Chip label="Promo" color="primary" size="small" variant="outlined" />
          )}
          {row.exclusive_deals_consent && (
            <Chip label="Deals" color="secondary" size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      name: "Status",
      sortable: true,
      width: "100px",
      selector: (row) => row.is_active,
      cell: (row) => (
        <Chip 
          label={row.is_active ? "Active" : "Opt-out"} 
          color={row.is_active ? "success" : "default"} 
          size="small" 
        />
      ),
    },
    {
      name: "Actions",
      width: "80px",
      cell: (row) => (
        <Box>
          {canDelete && row.is_active && (
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDelete(row.id, row.email || row.phone)}
              title="Revoke Consent"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  // Styles (Copied from reference)
  const customDataTableStyles = {
    table: { style: { backgroundColor: theme.palette.background.paper } },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"}`,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        "&:hover": {
          backgroundColor: theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
  };

  if (checkingPermissions) return <CircularProgress />;
  if (!canView) return <Alert severity="error">Access Denied</Alert>;

  return (
    <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px", bgcolor: "background.paper" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
            <Typography variant="h5" color="textPrimary">
            Consented <strong>Contacts</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
            Manage users who have opted-in for communication.
            </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {canAdd && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<UploadFileIcon />}
                onClick={() => setIsImportOpen(true)}
              >
                Import
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setIsFormOpen(true)}
              >
                Create Consent
              </Button>
            </>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {/* Search Bar */}
      <Box mb={2} display="flex" justifyContent="flex-end">
        <TextField
            size="small"
            variant="outlined"
            placeholder="Search email or phone..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ width: 300 }}
        />
      </Box>

      {/* Feedback Messages */}
      {error && (
        <Alert severity={error.type === "success" ? "success" : "error"} sx={{ mb: 2 }}>
          {typeof error === "string" ? error : error.message}
        </Alert>
      )}

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        progressPending={loading}
        responsive
        highlightOnHover
        customStyles={customDataTableStyles}
        noDataComponent={<Typography py={4}>No consented contacts found.</Typography>}
      />

      {/* Manual Create Modal */}
      <Modal open={isFormOpen} onClose={() => setIsFormOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "500px" },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            outline: "none"
          }}
        >
            <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Create Consent</Typography>
                <IconButton onClick={() => setIsFormOpen(false)} size="small"><CloseIcon /></IconButton>
            </Box>
            <ConsentedContactForm 
                onSuccess={handleSaveSuccess} 
                onCancel={() => setIsFormOpen(false)} 
            />
        </Box>
      </Modal>

      {/* Import Modal */}
      <Modal open={isImportOpen} onClose={() => setIsImportOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "700px" },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            outline: "none",
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
             <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Import Consents</Typography>
                <IconButton onClick={() => setIsImportOpen(false)} size="small"><CloseIcon /></IconButton>
            </Box>
            <ImportConsentModal 
                onSuccess={handleSaveSuccess} 
                onCancel={() => setIsImportOpen(false)} 
            />
        </Box>
      </Modal>
    </Paper>
  );
}